import React, { useMemo, useState } from 'react';
import { useAppState } from 'AppProvider';
import { ANALYTICS_EVENT, ANALYTICS_PROPERTY, ProductLine } from '@kouto/types';
import { useHistory } from 'react-router-dom';
import { getListingRoute } from 'utils/listings';
import { t } from 'i18next';
import { analyticsManager } from 'features/analytics';
import { useFormatPrice } from 'utils/listingsHelpers';
import useIsMobile from 'hooks/useIsMobile';
import { FeaturedListingV3 } from 'types/featuredListingV3';
import {
  Body,
  Button,
  FeaturedMeta,
  FeaturedParticipantsTotal,
  Footer,
  ButtonGray,
  PriceTag,
  StyledLink,
  TagButtonWrap,
  Title,
  WraperContent,
  Wrapper,
  WrapText,
} from './styles';
import HostedContainer from './HostedContainer';
import FeaturedImageContainer from './FeaturedImageContainer';

interface FeaturedExperienceItemProps {
  listing: FeaturedListingV3;
  isEven: boolean;
}

const FeaturedExperienceItem = ({
  listing,
  isEven,
}: FeaturedExperienceItemProps) => {
  const { settings } = useAppState(
    (state: Record<string, unknown>) => state.brand,
  );
  const exclusiveTagName = settings?.promotion?.exclusiveTag || t('exclusive');
  const [isHovered, setIsHovered] = useState(false);
  const history = useHistory();
  const isMobile = useIsMobile();
  const priceDisplay = useFormatPrice({
    advertisedPrice: listing.advertisedPrice,
    bookingAvailabilityMode: listing.bookingAvailabilityMode,
    maxParticipants: listing.maxParticipants,
    startingPrice: listing.startingPrice,
  });

  const href = useMemo(() => {
    return history.createHref(getListingRoute(listing));
  }, [history, listing]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (e && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onExperienceViewClick(e);
    }
  };

  const onExperienceViewClick = (
    e:
      | React.MouseEvent<HTMLAnchorElement>
      | React.KeyboardEvent<HTMLAnchorElement>,
  ) => {
    if (e) e.preventDefault(); // Add this line to handle both synthetic and native events
    analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_EXPERIENCE, {
      [ANALYTICS_PROPERTY.ExperienceId]: listing.id,
      [ANALYTICS_PROPERTY.ExperienceTitle]: listing.title ?? undefined,
      [ANALYTICS_PROPERTY.ExperienceCategory]:
        listing?.category?.name ?? undefined,
      [ANALYTICS_PROPERTY.Products]: JSON.stringify([
        {
          category: listing.category?.name,
          id: listing.id,
          index: 0,
          title: listing.title,
          startingPrice: listing.startingPrice,
        },
      ]),
    });
    history.push(getListingRoute(listing));
  };

  return (
    <Wrapper
      href={href}
      id={href.replace('#', '')}
      target="_parent"
      className={`featured-experience__wrapper-${isEven ? 'even' : 'odd'}`}
      onMouseOver={() => setIsHovered(true)}
      onFocus={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onBlur={() => setIsHovered(false)}
      tabIndex={0}
      onClick={onExperienceViewClick}
      onKeyDown={handleKeyDown}
    >
      <WraperContent>
        <WrapText className="featured-experience__wrap-text">
          <StyledLink className="featured-experience__styled-link">
            <Title className="featured-experience__item-title">
              {listing.title}
            </Title>
            <Body className="featured-experience__item-headline">
              {listing.summary}
            </Body>
            <TagButtonWrap
              className="featured-experience__tag-button tags"
              role="group"
            >
              {listing.category?.name && (
                <Button className="featured-experience__item-category-button">
                  {listing.category.name}
                </Button>
              )}
              {listing?.vibes
                ?.filter((vibe) => vibe)
                .map((vibe) => (
                  <ButtonGray
                    className="featured-experience__item-vibe-button"
                    id={`featured-experience-${listing?.id}-vibe-${vibe?.id}`}
                    key={`featured-view-vibe-${vibe?.id}`}
                  >
                    {vibe?.name}
                  </ButtonGray>
                ))}
            </TagButtonWrap>
            <PriceTag className="featured-experience__item-price-tag">
              <FeaturedMeta className="featured-experience__meta">
                <FeaturedParticipantsTotal className="featured-experience__participants-total">
                  {!listing.hidePrice &&
                    !!listing.startingPrice &&
                    `${t('fromListingCard')} `}
                  {!listing.hidePrice && priceDisplay}
                </FeaturedParticipantsTotal>
              </FeaturedMeta>
            </PriceTag>
            {listing.productLine !== ProductLine.RESERVE && listing.hostedBy ? (
              <HostedContainer hostedBy={listing.hostedBy} />
            ) : (
              !isMobile && <Footer className="featured-experience__footer" />
            )}
          </StyledLink>
        </WrapText>
      </WraperContent>
      <FeaturedImageContainer
        exclusiveTag={exclusiveTagName}
        isExclusive={listing.isExclusive}
        isHovered={isHovered}
        title={listing.title}
        medias={listing.medias}
        brandFlagName={
          listing.pointsEligible?.payEligible
            ? listing.pointsEligible?.org
            : undefined
        }
      />
    </Wrapper>
  );
};

export default FeaturedExperienceItem;
